import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Skip auth header for login and signup endpoints
    if (config.url.includes('/auth/login/') || config.url.includes('/auth/signup/')) {
      return config;
    }

    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Don't set Content-Type for FormData, let browser set it
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If error is not 401 or request has already been retried, reject
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Skip refresh token for login and signup endpoints
    if (originalRequest.url.includes('/auth/login/') || originalRequest.url.includes('/auth/signup/')) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;
    
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }
      
      const response = await axios.post(`${API_BASE_URL}/auth/token/refresh/`, {
        refresh: refreshToken
      });
      
      const { access } = response.data;
      localStorage.setItem('access_token', access);
      
      originalRequest.headers.Authorization = `Bearer ${access}`;
      return api(originalRequest);
    } catch (refreshError) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      // Do not redirect automatically here; let the app UI handle auth failures.
      return Promise.reject(refreshError);
    }
  }
);

// Auth API endpoints
export const authAPI = {
  login: (credentials) => api.post('/auth/login/', credentials),
  signup: (userData) => api.post('/auth/signup/', userData),
  logout: (data) => api.post('/auth/logout/', data),
  getProfile: () => api.get('/auth/user/'),
  updateProfile: (data) => api.put('/auth/user/', data),
  changePassword: (data) => api.post('/auth/password-change/', data),
  requestPasswordReset: (email) => api.post('/auth/password-reset/', { email }),
  resetPassword: (data) => api.post('/auth/password-reset/confirm/', data),
  deleteAccount: () => api.delete('/auth/user/delete/'),
};

// Leads API endpoints
export const leadsAPI = {
  getLeads: () => api.get('/leads/'),
  getLead: (id) => api.get(`/leads/${id}/`),
    // All params are optional and map directly to your Swagger fields
  getLeadsPaginated: ({
    status,
    created_after,
    created_before,
    search,
    ordering,
    page,
    page_size,
  } = {}) =>
    api.get("/leads/", {
      params: {
        status,
        created_after,
        created_before,
        search,
        ordering,
        page,
        page_size,
      },
    }),
  createLead: (data) => api.post('/leads/', data),
  updateLead: (id, data) => api.put(`/leads/${id}/`, data),
  deleteLead: (id) => api.delete(`/leads/${id}/`),
  bulkCreateLeads: (data) => api.post('/leads/bulk/', { leads: data }), // Wrap the array in a leads object
  getQualifiedLeads: (params) => api.get('/calls/proposal-status/', { params }),

  updateLeadStatus: (id, data) => api.put(`/leads/lead-update-status/${id}/`, data),

  uploadDocuments: (formData) => api.post('/leads/upload-document/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  bulkDeleteLeads: (ids) => api.post('/leads/bulk-delete/', { ids }),

  exportLeadsCSV: () => api.get('/leads/export-proposal/', { responseType: 'blob' }),

  moveToSeasonal: (data) => api.post('/leads/move-to-seasonal/', data),

};

// Calls API endpoints
export const callsAPI = {
  getCalls: () => api.get('/calls/'),
  getCall: (id) => api.get(`/calls/${id}/`),
  getRecentCalls: (params) => api.get('/dashboard/recent-calls/', { params }),
  getScheduledCalls: ({ page = 1, limit = 25, page_size = limit } = {}) =>
    api.get('/dashboard/scheduled-calls/', { params: { page, limit, page_size } }),
  refreshDashboardFeed: () => api.post('/dashboard/recent-calls/refresh/'),
  refreshScheduledCalls: () => api.post('/dashboard/scheduled-calls/refresh/'),
  initiateCall: (leadId) => api.post('/calls/initiate/', { lead_id: leadId }),
  completeCall: (data) => api.post('/calls/complete/', data),


  getInboundCalls: (params) =>
    api.get('/inbound_calls/calls/', { params }),
  
  // Export inbound calls
  exportInboundCallsCSV: () => api.get('/inbound_calls/export-csv/', { responseType: 'blob' }),
  
  // ElevenLabs Conversations API (for call history directly from ElevenLabs)
  getConversations: (params) => api.get('/calls/conversations/', { params }),
  getConversationDetails: (conversationId) => api.get(`/calls/conversations/${conversationId}/`),
  getConversationAudio: (conversationId) => api.get(`/calls/conversations/${conversationId}/audio/`, { responseType: 'blob' }),
  deleteConversation: (conversationId) => api.delete(`/calls/conversations/${conversationId}/`),
  
  // ElevenLabs Dashboard (statistics and analytics)
  getDashboard: () => api.get('/calls/dashboard/'),
  
  // Sync calls from ElevenLabs
  syncCalls: (maxAgeMinutes = 200) => api.post(`/calls/sync/?max_age_minutes=${maxAgeMinutes}`),
};

// Batch Calling API endpoints
export const batchCallsAPI = {
  // List all batch calls
  getBatchCalls: (params) => api.get('/calls/batch/', { params }),

  // Get scheduled calls from the legacy calls endpoint
  getScheduledCalls: ({ page = 1, limit = 25 } = {}) =>
    api.get('/calls/scheduled-calls/', { params: { page, limit } }),

  //delete scheduled call
  deleteScheduledCall: (id) => api.delete(`/calls/scheduled-calls/${id}/`),
  // Get specific batch call details
  getBatchCall: (id) => api.get(`/calls/batch/${id}/`),
  
  // Create new batch call
  createBatchCall: (data) => api.post('/calls/batch/create/', data),
  
  // Cancel batch call
  cancelBatchCall: (id) => api.post(`/calls/batch/${id}/cancel/`),

  // Retry failed batch call
  retryBatchCall: (id) => api.post(`/calls/batch/${id}/retry/`),

  // Sync a specific batch call
  syncBatchCall: (id) => api.post(`/calls/batch/${id}/sync/`),

  // Sync ALL pending/in_progress batch calls
  syncAllBatchCalls: () => api.post(`/calls/batch/sync/`),

  // Get batch call statistics
  getBatchStats: () => api.get('/calls/batch/stats/'),
};


// Stats API endpoints
export const statsAPI = {
  getStats: () => api.get('/stats/'),
};

// Request Review API endpoints
export const requestReviewAPI = {
  getClientProposalsForReview: (clientId) => api.get(`/request_review/client/${clientId}/proposals/`),
  initiateReviewCall: (data) => api.post('/request_review/call/', data),
  getReviewInsights: (params) => api.get('/request_review/reviews/insights/', { params }),
  uploadJobCloseoutSheet: (formData) => api.post('/request_review/upload-job-closeout-sheet/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
};

// Sales Calls API endpoints
export const salesCallsAPI = {
  // Leads
  importLeads: (formData) => api.post('/sales_calls/import-leads/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  createLead: (data) => api.post('/sales_calls/leads/', data),
  updateLead: (leadId, data) => api.patch(`/sales_calls/leads/${leadId}/`, data),
  deleteLead: (leadId) => api.delete(`/sales_calls/leads/${leadId}/delete/`),
  getLeads: (params) => api.get('/sales_calls/sales-leads/', { params }).then(r => r.data),
  
  // Calls
  makeSingleCall: (data) => api.post('/sales_calls/calls/single/', data),
  makeBatchCall: (data) => api.post('/sales_calls/calls/batch/', data),
  makeBatchCallFiltered: async (filters, callName) => {
    const response = await api.get('/sales_calls/sales-leads/', { params: { ...filters, page_size: 10000 } });
    const leadIds = response.data.results.map(lead => lead.id);
    return api.post('/sales_calls/calls/batch/', { lead_ids: leadIds, call_name: callName });
  },
  getCalls: (params) => api.get('/sales_calls/sales-calls/', { params }).then(r => r.data),

  // Export
  exportSalesCallsCSV: () => api.get('/sales_calls/export-csv/', { responseType: 'blob' }),
};


// Seasonal Calls API endpoints
export const seasonalCallsAPI = {
  // Leads
  importLeads: (formData) => api.post('/seasonal_calls/import-leads/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  createLead: (data) => api.post('/seasonal_calls/leads/', data),
  updateLead: (leadId, data) => api.patch(`/seasonal_calls/leads/${leadId}/`, data),
  deleteLead: (leadId) => api.delete(`/seasonal_calls/leads/${leadId}/delete/`),
  getLeads: (params) => api.get('/seasonal_calls/seasonal-leads/', { params }).then(r => r.data),
  
  // Calls
  makeSingleCall: (data) => api.post('/seasonal_calls/calls/single/', data),
  makeBatchCall: (data) => api.post('/seasonal_calls/calls/batch/', data),
  makeBatchCallFiltered: async (filters, callName) => {
    const response = await api.get('/seasonal_calls/seasonal-leads/', { params: { ...filters, page_size: 10000 } });
    const leadIds = response.data.results.map(lead => lead.id);
    return api.post('/seasonal_calls/calls/batch/', { lead_ids: leadIds, call_name: callName });
  },
  getCalls: (params) => api.get('/seasonal_calls/seasonal-calls/', { params }).then(r => r.data),

  // Export
  exportSeasonalCallsCSV: () => api.get('/seasonal_calls/export-csv/', { responseType: 'blob' }),
};

export const b2bCallsAPI = {
  // Leads
  importLeads: (formData) => api.post('/b2b_calls/import-leads/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  createLead: (data) => api.post('/b2b_calls/leads/', data),
  updateLead: (leadId, data) => api.patch(`/b2b_calls/leads/${leadId}/`, data),
  deleteLead: (leadId) => api.delete(`/b2b_calls/leads/${leadId}/delete/`),
  getLeads: (params) => api.get('/b2b_calls/b2b-leads/', { params }).then(r => r.data),
  
  // Calls
  makeSingleCall: (data) => api.post('/b2b_calls/calls/single/', data),
  makeBatchCall: (data) => api.post('/b2b_calls/calls/batch/', data),
  makeBatchCallFiltered: async (filters, callName) => {
    const response = await api.get('/b2b_calls/b2b-leads/', { params: { ...filters, page_size: 10000 } });
    const leadIds = response.data.results.map(lead => lead.id);
    return api.post('/b2b_calls/calls/batch/', { lead_ids: leadIds, call_name: callName });
  },
  getCalls: (params) => api.get('/b2b_calls/b2b-calls/', { params }).then(r => r.data),

  // Export
  exportB2bCallsCSV: () => api.get('/b2b_calls/export-csv/', { responseType: 'blob' }),
};

// Website Chatbot Leads API endpoints
export const websiteChatbotLeadsAPI = {
  getLeads: (params) => api.get('/website_chatbot/leads/', { params }).then(r => r.data),
  getLead: (id) => api.get(`/website_chatbot/leads/${id}/`).then(r => r.data),
  createLead: (data) => api.post('/website_chatbot/leads/', data),
  updateLead: (id, data) => api.put(`/website_chatbot/leads/${id}/`, data),
  deleteLead: (id) => api.delete(`/website_chatbot/leads/${id}/`),
};

// SMS API endpoints
export const smsAPI = {
  getInboundSMS: (params) => api.get('/inbound_calls/sms/', { params }),
};

// Site Reports API endpoints (site visit reports submitted from the iOS app)
export const siteReportsAPI = {
  getSiteReports: (params) => api.get('/site_visit/', { params }),
  getSiteReport: (id) => api.get(`/site_visit/${id}/`),
  updateSiteReport: (id, data) => {
    const isFormData = data instanceof FormData;
    return api.patch(`/site_visit/${id}/`, data, isFormData ? {
      headers: { 'Content-Type': 'multipart/form-data' },
    } : undefined);
  },
  deleteSiteReport: (id) => api.delete(`/site_visit/${id}/`),
};

export default api; 